import { useAppSelector } from "@/app/hooks";
import CBRELogo from "@/assets/images/cbreLogo.svg?react";
import type { Contact } from "@/features/survey/types";
import { useGetSurveyGroupContactsQuery } from "@/services/surveyGroupService";
import { formatCoverDate } from "@/shared/utils/formatCoverDate";
import {
  getShortlistedProperties,
  isShortlistEnabled,
} from "@/shared/utils/propertyUtils";
import { EmeraldIcon } from "@emerald-react/icon";
import { useState } from "react";
import { Link } from "react-router";
import ExploreMarketsCard from "./components/ExploreMarketsCard";
import InteractiveSurveyContactPreviewList from "./components/InteractiveSurveyContactPreviewList";
import MarketsDropdown from "./components/MarketsDropdown";

const defaultCoverImage = "/warehouse3.jpg";

/**
 * Interactive Survey Group Cover Page
 * Landing page for a survey group (multi-market): shows the group-level
 * overview; clicking a market tab navigates into that survey's own
 * Market Map (its full single-survey experience), leaving this page.
 */
const InteractiveSurveyGroupCover = () => {
  const { currentSurveyGroup, groupSurveys } = useAppSelector(
    (state) => state.interactiveSurvey,
  );
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logoImgError, setLogoImgError] = useState(false);
  const [imageError, setImageError] = useState(false);

  const surveys = groupSurveys ?? [];

  // Survey groups don't embed contacts, so the Overview tab fetches them separately.
  const { data: groupContacts } = useGetSurveyGroupContactsQuery(
    currentSurveyGroup?.surveyGroupId || "",
    { skip: !currentSurveyGroup?.surveyGroupId },
  );

  if (!currentSurveyGroup) {
    return null;
  }

  const showShortlist = isShortlistEnabled(null, currentSurveyGroup);
  const shortlistCount = surveys.reduce(
    (total, survey) => total + getShortlistedProperties(survey).length,
    0,
  );
  const shortlistPath = `/interactive/group/${currentSurveyGroup.surveyGroupId}/shortlist`;

  const { coverPageInfo, clientLogoInfo } = currentSurveyGroup;

  const coverTitle = coverPageInfo?.coverTitle;
  const coverSubTitle = coverPageInfo?.coverSubTitle;
  const showClientName = coverPageInfo?.isDisplayClientName;
  const clientName = coverPageInfo?.clientName;
  const coverDateDisplay = formatCoverDate(coverPageInfo?.coverDate);

  const showClientLogo = clientLogoInfo?.isDisplayClientLogo;
  const clientLogoUrl = clientLogoInfo?.clientLogo;
  const isClientLogoTransparent = clientLogoInfo?.isClientLogoTransparent;
  const unrestrictedLogoSize = clientLogoInfo?.isUnrestrictedLogoSize;

  const groupBackgroundImage = coverPageInfo?.coverBackgroundImage?.cloudfrontUrl
    ? `${coverPageInfo.coverBackgroundImage.cloudfrontUrl}?d=web`
    : undefined;
  const backgroundImageUrl =
    !imageError && groupBackgroundImage ? groupBackgroundImage : defaultCoverImage;

  return (
    <div className="min-h-screen bg-cbre-green flex flex-col">
      {/* Header — mirrors InteractiveSurveyMenuBar's main-nav styling */}
      <header className="main-nav">
        <div className="flex flex-col lg:flex-row items-center">
          <CBRELogo className="w-24 h-6.5" />
          <div className="header-divider"></div>

          <div className="hidden lg:block desktop-nav-container">
            <div className="flex items-baseline nav-items">
              <span className="nav-item active">Overview</span>
              <MarketsDropdown surveys={surveys} />
              {showShortlist && (
                <Link to={shortlistPath} className="nav-item">
                  Shortlist
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#17e88f] text-xs font-bold text-[#003f2d]">
                    {shortlistCount}
                  </span>
                </Link>
              )}
            </div>
          </div>
        </div>
        <div className="lg:flex items-stretch hidden">
          <a
            href="https://www.cbre.com/about-us/disclaimer-terms-of-use"
            target="_blank"
            rel="noopener noreferrer"
            className="nav-item"
          >
            Disclaimer
          </a>
        </div>
        <div className="lg:hidden flex gap-x-2 items-center">
          <p className="text-white text-base font-medium truncate">Overview</p>
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-200 hover:text-green-300 focus:outline-none"
          >
            <span className="sr-only">
              {isMobileMenuOpen ? "Close menu" : "Open menu"}
            </span>
            <EmeraldIcon icon={isMobileMenuOpen ? "close" : "menu"} />
          </button>
        </div>
      </header>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-150 top-18">
          <div className="px-2 pt-2 pb-3 space-y-1 w-screen h-screen overflow-auto sm:px-3 bg-cbre-green">
            <p className="block px-3 py-2 text-sm font-semibold text-white underline">
              Overview
            </p>
            <p className="block px-3 py-2 text-sm font-semibold text-white">
              {`Markets (${surveys.length})`}
            </p>
            {surveys.map((survey) => (
              <Link
                key={survey.id}
                to={`/interactive/${survey.id}/marketmap`}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-6 py-2 rounded-md text-sm font-normal text-gray-200 hover:text-green-300 transition-colors"
              >
                {survey.name}
              </Link>
            ))}

            {showShortlist && (
              <Link
                to={shortlistPath}
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-normal text-gray-200 hover:text-green-300 transition-colors"
              >
                Shortlist
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#17e88f] text-xs font-bold text-[#003f2d]">
                  {shortlistCount}
                </span>
              </Link>
            )}

            <a
              href="https://www.cbre.com/about-us/disclaimer-terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
              className="block px-3 py-2 rounded-md text-sm font-normal text-gray-200 hover:text-green-300 transition-colors"
            >
              Disclaimer
            </a>
          </div>
        </div>
      )}

      {/* Cover content — same two-panel layout as the single-survey cover page */}
      <div className="flex flex-col flex-1 lg:flex-row relative bg-cbre-green overflow-y-auto">
        {/* Left Panel */}
        <div className="w-full lg:w-1/2 relative text-white bg-cbre-green lg:min-h-full order-1 lg:order-1 flex flex-col lg:overflow-y-auto">
          <div
            className="flex-1 flex flex-col justify-center relative px-4 sm:px-6 md:px-8 lg:px-12"
            style={{ paddingTop: "clamp(0, 12vw, 200px)" }}
          >
            <main
              className="lg:pr-20 max-w-2xl w-full mb-5 lg:mb-[50%] mt-[50px]"
              aria-label="Survey information"
            >
              <div>
                <h1
                  className="text-white mb-2 fancy"
                  style={{
                    fontSize: "clamp(32px, 4vw, 72px)",
                    fontWeight: 400,
                    lineHeight: "clamp(28px, 4.5vw, 72px)",
                    letterSpacing: "0.5px",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                    marginBottom: "24px",
                  }}
                >
                  {coverTitle || "Property Survey"}
                </h1>
              </div>
              <div
                className="bg-white mb-4 sm:mb-6"
                style={{
                  height: "clamp(2px, 0.5vw, 3px)",
                  width: "calc(100% + 5rem)",
                  margin: "0",
                  marginBottom: "clamp(16px, 3vw, 24px)",
                  borderRadius: "2px",
                  position: "relative",
                  zIndex: 2,
                }}
              />
              {coverSubTitle && (
                <div className="mb-4 sm:mb-6 z-10 wrap-break-word overflow-hidden">
                  <p
                    className="text-white"
                    style={{
                      fontFamily: '"Calibre", "Helvetica Neue", Arial, sans-serif',
                      fontSize: "20px",
                      fontWeight: 500,
                      lineHeight: "120%",
                      letterSpacing: "0.3px",
                    }}
                  >
                    {coverSubTitle}
                  </p>
                </div>
              )}
              <div className="mb-6 sm:mb-8">
                <div className="flex flex-col text-white gap-1">
                  {showClientName && clientName && (
                    <span
                      style={{
                        fontFamily: '"Calibre", "Helvetica Neue", Arial, sans-serif',
                        fontSize: "16px",
                        fontWeight: 500,
                        opacity: 0.9,
                        width: "100%",
                        overflowWrap: "break-word",
                        wordBreak: "break-word",
                      }}
                    >
                      {clientName}
                    </span>
                  )}
                  <span className="updated-date">{coverDateDisplay}</span>
                </div>
              </div>
              {showClientLogo && clientLogoUrl && !logoImgError && (
                <div
                  className="mt-4 sm:mt-6 lg:mt-8"
                  style={{ display: "flex", alignItems: "flex-start" }}
                >
                  <img
                    src={clientLogoUrl}
                    alt="Client Logo"
                    style={{
                      objectFit: "contain",
                      height: "auto",
                      width: "auto",
                      maxWidth: unrestrictedLogoSize
                        ? "clamp(200px, 60vw, 500px)"
                        : "125px",
                      maxHeight: unrestrictedLogoSize
                        ? "clamp(150px, 60vw, 500px)"
                        : "125px",
                      backgroundColor: isClientLogoTransparent
                        ? "transparent"
                        : "rgba(255,255,255,0.95)",
                      padding: isClientLogoTransparent ? "0" : "4px",
                      boxShadow: isClientLogoTransparent
                        ? "none"
                        : "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                    onError={() => setLogoImgError(true)}
                  />
                </div>
              )}
            </main>
          </div>
        </div>

        {/* Right Panel - Cover Image */}
        <div className="right-panel w-full lg:w-1/2 relative order-2 lg:order-2 flex-1 min-h-[40vh] sm:min-h-[50vh] lg:min-h-full">
          {/* absolute (not h-full) — percentage height doesn't reliably resolve against a flex-grown parent here */}
          <div className="absolute inset-0">
            <img
              src={backgroundImageUrl}
              alt={
                backgroundImageUrl !== defaultCoverImage
                  ? "Survey Cover"
                  : "Warehouse Placeholder"
              }
              className="h-full w-full object-cover transition-opacity duration-300"
              style={{ objectPosition: "center center" }}
              onError={() => {
                if (!imageError) {
                  setImageError(true);
                }
              }}
            />
            <div
              className="absolute inset-0 bg-linear-to-t from-black/20 to-transparent lg:hidden pointer-events-none"
              style={{ zIndex: 1 }}
            />
            <ExploreMarketsCard surveys={surveys} />
          </div>
          {/* Contact Cards */}
          <div className="contact-area">
            {groupContacts && groupContacts.length > 0 && (
              <InteractiveSurveyContactPreviewList
                surveyContactList={groupContacts as Contact[]}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveSurveyGroupCover;

